import { h, toast, confirmDialog, rowAction, rowActions } from '../components/dom.js';
import { renderShell, pageHead, portalFooter } from '../components/shell.js';
import { api, unsupported } from '../api/client.js';
import { createPasskey, webauthnSupported } from '../crypto/webauthn.js';
import { runPasskeySignals, browserSignaller } from '../crypto/passkey-signal.js';
import { config } from '../config.js';
import { encode, Ecc } from '../vendor/qr.js';
import { drawQrCenterLogo } from '../lib/qr-logo.js';
import { buildEncryptedZip, buildEncryptedZipMulti } from '../vendor/zip-crypto.js';
import {
  buildIdentityJson, buildProfileJson, buildContactsJson, buildSettingsJson,
  buildActivityJson, buildReadme, buildSchema, exportFileName, toJsonBytes,
} from '../lib/data-export.js';
import type { AuthMethod, Identity } from '../api/types.js';
import { sortAuthenticators } from './totp-sort.js';
import { getUserTimeZone, setUserTimeZone, listTimeZones, formatInZone, dateTimeEl, tzOffsetLabel } from '../lib/timezone.js';
import { emailErrorMessage, resendSecondsRemaining } from './email-copy.js';
import { router } from '../router/router.js';

export async function renderSettings(_params?: Record<string, string>, query?: URLSearchParams): Promise<void> {
  const main = renderShell('/settings');
  main.append(pageHead('Settings', 'Authentication, account, and data controls.'));

  const [passkeysRes, totpRes, me, pub] = await Promise.all([
    api.auth.passkeys(),
    api.auth.totpList().catch(() => ({ authenticators: [] })),
    api.me(),
    unsupported.getPublicProfile(),
  ]);

  main.append(passkeySection(passkeysRes.data));
  main.append(totpSection(totpRes.authenticators || []));
  main.append(recoverySection(me));
  main.append(profileSection(me));
  main.append(emailSection());
  main.append(notificationsSection(me));
  main.append(contactPrefsSection(me));
  main.append(timezoneSection());
  main.append(publicSection(pub));
  main.append(dataSection(me));
  main.append(portalFooter());

  // Deep-link support: #/settings?section=<id> scrolls that section into view
  // and briefly highlights it. Used by the dashboard "Add email" prompt
  // (?section=email). Runs after the DOM is appended so the target exists.
  const section = query?.get('section');
  if (section) {
    const target = main.querySelector('#section-' + section) as HTMLElement | null;
    if (target) {
      requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        target.classList.add('section-flash');
        setTimeout(() => target.classList.remove('section-flash'), 1600);
      });
    }
  }
}

function card(title: string, sub: string, ...body: (HTMLElement | null)[]): HTMLElement {
  return h('div', { class: 'card', style: 'margin-bottom:22px' },
    h('h3', { style: 'margin-top:0' }, title), h('p', { class: 'muted' }, sub), ...body.filter(Boolean) as HTMLElement[]);
}

function passkeySection(passkeys: AuthMethod[]): HTMLElement {
  const list = h('div', { class: 'stack' });
  const render = () => list.replaceChildren(...passkeys.map((m) => passkeyRow(m, passkeys, render)));
  render();

  const addBtn = h('button', { class: 'btn btn-primary btn-sm' }, 'Add a passkey');
  addBtn.addEventListener('click', async () => {
    if (!webauthnSupported()) { toast('This browser does not support passkeys.', 'warn'); return; }
    addBtn.setAttribute('disabled', 'true');
    try {
      // Already-bound session: the bearer token authenticates begin/finish, so no
      // DPoP keypair is needed here (unlike signup, which mints a fresh session).
      const ceremony = await api.auth.addPasskeyBegin();
      const credential = await createPasskey(ceremony.options);
      const label = (prompt('Name this passkey', 'Passkey') || 'Passkey').trim() || 'Passkey';
      const created = await api.auth.addPasskeyFinishCeremony(ceremony.ceremony_id, credential, label);
      passkeys.push({ id: created.id, label: created.label, kind: 'passkey' } as AuthMethod);
      render();
      toast('Passkey added', 'ok');
    } catch (e) {
      toast((e as Error).message || 'Could not add passkey', 'danger');
    } finally {
      addBtn.removeAttribute('disabled');
    }
  });

  return card('Passkeys', 'Devices and security keys that can sign in to your account.', list, addBtn);
}

function passkeyRow(m: AuthMethod, all: AuthMethod[], refresh: () => void): HTMLElement {
  const rename = rowAction('Rename', async () => {
    const next = (prompt('Rename passkey', m.label || 'Passkey') || '').trim();
    if (!next || next === (m.label || '')) return;
    try { await api.auth.renamePasskey(m.id, next); m.label = next; toast('Renamed', 'ok'); refresh(); }
    catch (err) { toast((err as Error).message, 'danger'); }
  });
  const remove = rowAction('Remove', async () => {
    if (all.length <= 1) { toast('You must keep at least one passkey.', 'warn'); return; }
    if (!confirmDialog(`Remove "${m.label}"? You can no longer sign in with this device.`)) return;
    try { await api.auth.removePasskey(m.id); const i = all.indexOf(m); if (i >= 0) all.splice(i, 1); toast('Removed', 'ok'); refresh(); }
    catch (err) { toast((err as Error).message, 'danger'); }
  }, { variant: 'danger' });
  return h('div', { class: 'row between', style: 'padding:10px 0;border-bottom:1px solid var(--border)' },
    h('div', {}, h('strong', {}, m.label || 'Passkey'),
      h('br'), h('small', { class: 'muted' }, 'FIDO2 · last used ', m.last_used_at ? dateTimeEl(m.last_used_at, { dateOnly: true }) : '\u2014')),
    rowActions(rename, remove));
}

// Full authenticator-management panel: lists each enrolled TOTP authenticator
// (newest first), lets the user remove a specific one (with confirmation), and
// add a new one via the begin -> QR -> verify enroll flow with a custom label.
// The section owns its own state and re-renders in place after add/remove.
function totpSection(initial: AuthMethod[]): HTMLElement {
  let auths = [...initial];

  const sub = h('p', { class: 'muted' }, '');
  const list = h('div', { class: 'stack' });
  const out = h('div', {});

  const setSub = () => {
    (sub.firstChild as Text | null)?.remove();
    sub.textContent = auths.length > 0
      ? 'A time-based one-time code app is an active backup second factor. You can add or remove authenticators below.'
      : 'Add a TOTP app (Google Authenticator, Authy, 1Password…) as a backup second factor.';
  };

  const refetch = async () => {
    try {
      const res = await api.auth.totpList();
      auths = res.authenticators || [];
    } catch { /* keep last-known list */ }
    renderList();
  };

  const renderList = () => {
    setSub();
    if (auths.length === 0) {
      list.replaceChildren(h('p', { class: 'muted' }, h('small', {}, 'No authenticator apps enrolled yet.')));
      return;
    }
    list.replaceChildren(...sortAuthenticators(auths).map(totpRow));
  };

  const totpRow = (m: AuthMethod): HTMLElement => {
    const rename = rowAction('Rename', async () => {
      const next = (prompt('Rename authenticator', m.label || 'Authenticator app') || '').trim();
      if (!next || next === (m.label || '')) return;
      try { await api.auth.renameTotp(m.id, next); m.label = next; toast('Renamed', 'ok'); await refetch(); }
      catch (err) { toast((err as Error).message, 'danger'); }
    });
    const remove = rowAction('Remove', async () => {
      if (!confirmDialog(`Remove "${m.label || 'Authenticator app'}"? This authenticator can no longer generate valid codes.`)) return;
      try { await api.auth.deleteTotp(m.id); toast('Authenticator removed', 'ok'); await refetch(); }
      catch (err) { toast((err as Error).message, 'danger'); }
    }, { variant: 'danger' });
    const enrolled = m.created_at ? dateTimeEl(m.created_at, { dateOnly: true }) : h('span', { class: 'muted' }, '—');
    return h('div', { class: 'row between', style: 'padding:10px 0;border-bottom:1px solid var(--border)' },
      h('div', {}, h('strong', {}, m.label || 'Authenticator app'),
        h('br'), h('small', { class: 'muted' }, 'TOTP · added ', enrolled,
          ...(m.last_used_at ? [' · last used ', dateTimeEl(m.last_used_at, { dateOnly: true })] : []))),
      rowActions(rename, remove));
  };

  const addBtn = h('button', { class: 'btn btn-primary btn-sm' }, 'Add authenticator');
  addBtn.addEventListener('click', async () => {
    const labelInput = h('input', { class: 'input', placeholder: 'Label (e.g. iPhone, 1Password)', value: 'Authenticator app', style: 'max-width:260px' }) as HTMLInputElement;
    const startBtn = h('button', { class: 'btn btn-ghost btn-sm' }, 'Start enrollment');
    out.replaceChildren(h('div', { class: 'row', style: 'gap:8px;margin-top:8px' }, labelInput, startBtn));
    startBtn.addEventListener('click', async () => {
      // Guard against a double-click firing two enrollments (each begin created a
      // pending authenticator that surfaced as a duplicate "added twice" row).
      if (startBtn.hasAttribute('disabled')) return;
      startBtn.setAttribute('disabled', 'true');
      const label = labelInput.value.trim() || 'Authenticator app';
      try {
        const enroll = await api.auth.totpBegin(label);
        const qrWrap = h('div', { style: 'margin:10px 0' });
        try { qrWrap.append(totpQrCanvas(enroll.otpauth_url)); }
        catch { qrWrap.append(h('p', { class: 'muted' }, h('small', {}, 'Could not render QR — use the manual key below.'))); }
        const input = h('input', { class: 'input', placeholder: '6-digit code', style: 'max-width:180px' }) as HTMLInputElement;
        const verify = h('button', { class: 'btn btn-primary btn-sm' }, 'Verify');
        verify.addEventListener('click', async () => {
          if (verify.hasAttribute('disabled')) return;
          verify.setAttribute('disabled', 'true');
          try {
            await api.auth.totpFinish(enroll.id, input.value.trim());
            toast('Authenticator enabled', 'ok');
            out.replaceChildren();
            await refetch();
          } catch (e) { toast((e as Error).message, 'danger'); verify.removeAttribute('disabled'); }
        });
        out.replaceChildren(...[
          h('p', {}, `Scan this QR code with your authenticator app for "${label}", then enter the 6-digit code it shows:`),
          qrWrap,
          h('details', { style: 'margin:6px 0' },
            h('summary', { class: 'muted', style: 'cursor:pointer' }, h('small', {}, "Can't scan? Enter the key manually")),
            h('p', { class: 'muted', style: 'margin-top:6px' }, h('small', {}, 'Secret key: '), h('code', { style: 'word-break:break-all' }, enroll.secret))),
          h('div', { class: 'row' }, input, verify),
        ].filter((x): x is HTMLElement => x != null));
      } catch (e) { toast((e as Error).message, 'danger'); startBtn.removeAttribute('disabled'); }
    });
  });

  renderList();
  return card('Authenticator apps (TOTP)', '', sub, list, addBtn, out);
}

// Render a crisp, scannable QR of an otpauth:// URI into a canvas (hi-DPI aware,
// 4-module quiet zone). Uses HIGH ECC (30% recovery) so the centered VirtualID
// logo overlay stays scannable.
function totpQrCanvas(otpauthUrl: string, cssSize = 220): HTMLCanvasElement {
  const { size, modules } = encode(otpauthUrl, Ecc.HIGH);
  const quiet = 4;
  const total = size + quiet * 2;
  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  const scale = Math.max(1, Math.floor((cssSize * dpr) / total));
  const px = total * scale;
  const canvas = h('canvas', { width: px, height: px, style: `width:${cssSize}px;height:${cssSize}px;border-radius:8px;background:#fff` }) as HTMLCanvasElement;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, px, px);
  ctx.fillStyle = '#000';
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++)
      if (modules[y][x]) ctx.fillRect((x + quiet) * scale, (y + quiet) * scale, scale, scale);
  drawQrCenterLogo(ctx, px);
  return canvas;
}

// Prompt for an encryption password with a confirm field. Resolves to the
// password string, or null if the user cancels. Requires both entries to match
// and be non-empty; warns (but does not block) on short passwords.
function promptEncryptionPassword(): Promise<string | null> {
  return new Promise((resolve) => {
    const pw1 = h('input', { class: 'input', type: 'password', placeholder: 'Encryption password', autocomplete: 'new-password' }) as HTMLInputElement;
    const pw2 = h('input', { class: 'input', type: 'password', placeholder: 'Confirm password', autocomplete: 'new-password' }) as HTMLInputElement;
    const err = h('p', { class: 'muted', style: 'color:var(--danger);min-height:1em;margin:4px 0 0' }, h('small', {}, ''));

    const overlay = h('div', {
      style: 'position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;z-index:1000',
    });
    let settled = false;
    const finish = (v: string | null) => { if (settled) return; settled = true; overlay.remove(); resolve(v); };

    const cancelBtn = h('button', { class: 'btn btn-ghost btn-sm' }, 'Cancel');
    cancelBtn.addEventListener('click', () => finish(null));

    const okBtn = h('button', { class: 'btn btn-primary btn-sm' }, 'Download') as HTMLButtonElement;
    const submit = () => {
      const a = pw1.value, b = pw2.value;
      if (!a) { (err.firstChild as HTMLElement).textContent = 'Password cannot be empty.'; return; }
      if (a !== b) { (err.firstChild as HTMLElement).textContent = 'Passwords do not match.'; return; }
      finish(a);
    };
    okBtn.addEventListener('click', submit);

    const warn = h('p', { class: 'muted', style: 'min-height:1em;margin:4px 0 0' }, h('small', {}, ''));
    const onInput = () => {
      (err.firstChild as HTMLElement).textContent = '';
      (warn.firstChild as HTMLElement).textContent = pw1.value && pw1.value.length < 8 ? 'Tip: use at least 8 characters for a stronger backup.' : '';
    };
    pw1.addEventListener('input', onInput);
    pw2.addEventListener('input', onInput);
    pw2.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') submit(); });

    const modal = h('div', { class: 'card', style: 'max-width:460px;width:90%' },
      h('h3', { style: 'margin-top:0' }, 'Encrypt recovery backup'),
      h('p', { class: 'muted' }, h('small', {}, 'The download is a password-protected ZIP using the legacy ZipCrypto method, so Windows Explorer can open it without extra software. You will need this password to open it — it cannot be recovered. (ZipCrypto is weaker than AES; for a highly sensitive backup, open it with 7-Zip.)')),
      h('div', { class: 'field' }, pw1),
      h('div', { class: 'field' }, pw2),
      warn, err,
      h('div', { class: 'row', style: 'justify-content:flex-end;gap:8px;margin-top:12px' }, cancelBtn, okBtn),
    );
    overlay.append(modal);
    document.body.append(overlay);
    pw1.focus();
  });
}

// Trigger a browser download of `bytes` as a file named `name`.
function downloadBytes(bytes: Uint8Array, name: string, mime = 'application/zip'): void {
  const blob = new Blob([bytes as any], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = h('a', { href: url, download: name, style: 'display:none' }) as HTMLAnchorElement;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function recoverySection(me: Identity): HTMLElement {
  const out = h('div', {});
  const btn = h('button', { class: 'btn btn-ghost btn-sm' }, 'Generate recovery key');
  btn.addEventListener('click', async () => {
    if (!confirmDialog('Generate a new recovery key? Store it somewhere safe and offline. Any previous key stops working.')) return;
    try {
      const { recovery_key } = await api.auth.recoveryKey();
      const keyCode = h('code', { style: 'flex:1;padding:12px;background:var(--bg-soft);border-radius:8px;word-break:break-all' }, recovery_key);
      const copyBtn = h('button', { class: 'btn btn-ghost btn-sm', type: 'button', style: 'align-self:flex-start' }, 'Copy') as HTMLButtonElement;
      copyBtn.addEventListener('click', () => {
        const done = () => toast('Recovery key copied', 'ok');
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(recovery_key).then(done).catch(() => toast('Copy failed — select and copy manually.', 'warn'));
        else toast('Copy not supported — select and copy manually.', 'warn');
      });

      const downloadBtn = h('button', { class: 'btn btn-ghost btn-sm', type: 'button', style: 'align-self:flex-start' }, 'Download') as HTMLButtonElement;
      downloadBtn.addEventListener('click', async () => {
        const password = await promptEncryptionPassword();
        if (password == null) return; // cancelled — silent
        try {
          const rawUser = me.username || me.handle || '';
          const user = (rawUser.replace(/[^A-Za-z0-9_-]/g, '') || 'user');
          const now = new Date();
          const p2 = (n: number) => String(n).padStart(2, '0');
          const stamp = `${now.getFullYear()}${p2(now.getMonth() + 1)}${p2(now.getDate())}${p2(now.getHours())}${p2(now.getMinutes())}${p2(now.getSeconds())}`;
          const base = `VirtualID_RecoveryCode_${user}_${stamp}`;
          const txt = new TextEncoder().encode(`VirtualID recovery code\n\n${recovery_key}\n`);
          const zip = await buildEncryptedZip(`${base}.txt`, txt, password);
          downloadBytes(zip, `${base}.zip`);
          toast('Encrypted backup downloaded', 'ok');
        } catch (e) { toast((e as Error).message || 'Download failed', 'danger'); }
      });

      out.replaceChildren(
        h('div', { class: 'row', style: 'align-items:flex-start;gap:8px' }, keyCode, copyBtn, downloadBtn),
        h('p', { class: 'muted' }, h('small', {}, 'This is the only time it will be shown. It can restore access if you lose every passkey.')));
    } catch (e) { toast((e as Error).message, 'warn'); }
  });
  return card('Recovery key', 'A one-time secret that restores account access if you lose all your devices.', btn, out);
}

// Notifications: opt-out email toggles delivered to the user's VERIFIED primary
// email. Both default ON. Uses the same button.toggle control as the contact and
// field toggles. Each row PATCHes /me independently and reverts its control on
// failure. A hint reminds the user delivery needs a verified primary email
// (configured in the Primary email section directly above).
function notificationsSection(me: Identity): HTMLElement {
  const makeRow = (
    title: string,
    desc: string,
    initial: boolean,
    patch: (v: boolean) => Promise<unknown>,
  ): HTMLElement => {
    let value = initial;
    const tog = h('button', { class: 'toggle' + (value ? ' on' : ''), type: 'button',
      'aria-label': value ? `${title} on` : `${title} off` });
    tog.addEventListener('click', async () => {
      const next = !value;
      tog.setAttribute('disabled', '');
      try {
        await patch(next);
        value = next;
        tog.className = 'toggle' + (next ? ' on' : '');
        tog.setAttribute('aria-label', next ? `${title} on` : `${title} off`);
        toast(next ? 'Notification enabled' : 'Notification disabled', 'ok');
      } catch (e) {
        toast((e as Error).message || 'Could not update setting', 'danger'); // unchanged on failure
      } finally {
        tog.removeAttribute('disabled');
      }
    });
    return h('div', { class: 'row between', style: 'padding:10px 0;gap:12px;align-items:center;border-bottom:1px solid var(--border)' },
      h('span', {}, h('strong', {}, title),
        h('br'), h('small', { class: 'muted' }, desc)),
      tog);
  };

  const acceptRow = makeRow(
    'New connections',
    'Email me when someone accepts my invitation or adds me to their contacts.',
    me.notify_on_accept !== false, // default ON
    (v) => api.updateMe({ notify_on_accept: v }),
  );
  const updateRow = makeRow(
    'Contact updates',
    'Email me when one of my contacts updates their contact information.',
    me.notify_on_contact_update !== false, // default ON
    (v) => api.updateMe({ notify_on_contact_update: v }),
  );

  const hint = h('p', { class: 'muted', style: 'margin:10px 0 0' },
    h('small', {}, 'Notifications are sent to your verified primary email. Add and verify one above to receive them.'));

  return card('Notifications', 'Choose which email updates VirtualID sends you.', acceptRow, updateRow, hint);
}

// Toggle for auto-accepting reciprocal share-backs. When ON, a contact who
// subscribes to me and shares a profile back is accepted automatically; when
// OFF, share-backs arrive grayed/pending on My Contacts with an Add button.
function contactPrefsSection(me: Identity): HTMLElement {
  let value = me.auto_accept_contacts !== false; // default ON
  // Uses the SAME toggle control as field enable/disable in the profile view
  // (button.toggle[.on]) instead of a raw checkbox.
  const tog = h('button', { class: 'toggle' + (value ? ' on' : ''), type: 'button',
    'aria-label': value ? 'Auto-accept on' : 'Auto-accept off' });
  tog.addEventListener('click', async () => {
    const next = !value;
    tog.setAttribute('disabled', '');
    try {
      await api.updateMe({ auto_accept_contacts: next });
      value = next;
      tog.className = 'toggle' + (next ? ' on' : '');
      tog.setAttribute('aria-label', next ? 'Auto-accept on' : 'Auto-accept off');
      toast(next ? 'Auto-accept enabled' : 'Auto-accept disabled', 'ok');
    } catch (e) {
      toast((e as Error).message || 'Could not update setting', 'danger'); // unchanged on failure
    } finally {
      tog.removeAttribute('disabled');
    }
  });
  const row = h('div', { class: 'row between', style: 'padding:10px 0;gap:12px;align-items:center' },
    h('span', {}, h('strong', {}, 'Auto-accept contacts which subscribe to me'),
      h('br'), h('small', { class: 'muted' },
        'When on, people who subscribe to you and share a profile back are added automatically. When off, they appear grayed on My Contacts with an Add button.')),
    tog);
  return card('Contacts', 'Control how reciprocal share-backs are handled.', row);
}

// timezoneSection lets the user pick the display timezone for all date/times in
// the app. Pure client-side preference (localStorage) — no backend. On change we
// save, toast, update the live-preview line, and re-render the current view so
// all visible timestamps refresh immediately (no full page reload needed).
function timezoneSection(): HTMLElement {
  const current = getUserTimeZone();
  const zones = listTimeZones(current);
  const sel = h('select', { class: 'select' },
    ...zones.map((z) => h('option', { value: z, selected: z === current }, `${z} (${tzOffsetLabel(z)})`)),
  ) as HTMLSelectElement;
  sel.value = current;

  const preview = h('p', { class: 'muted', style: 'margin-top:8px' },
    h('small', {}, 'Current time: ', h('span', {}, formatInZone(Date.now())))) as HTMLElement;
  const previewSpan = preview.querySelector('span') as HTMLElement;

  sel.addEventListener('change', () => {
    const tz = sel.value;
    setUserTimeZone(tz);
    previewSpan.textContent = formatInZone(Date.now(), { tz });
    toast('Timezone updated', 'ok');
    // Re-render the current route so every already-drawn timestamp refreshes
    // into the new zone without a hard reload.
    router.navigate(router.current());
  });

  const row = h('div', { class: 'row between', style: 'padding:10px 0;gap:12px;align-items:center' },
    h('span', {}, h('strong', {}, 'Display timezone'),
      h('br'), h('small', { class: 'muted' },
        'All dates and times in the app are shown in this zone, labeled with its abbreviation and UTC offset. Hover any date to see the UTC time.')),
    sel);
  return card('Timezone', 'Choose the timezone used to display date and time throughout the app.', row, preview);
}

// Primary-email section: self-owned state that re-renders in place after every
// add / verify / resend / remove (mirrors totpSection). Three visual states:
//   - none:      email input + "Add email"
//   - pending:   greyed email + "Unverified" badge, Remove, code input + Confirm,
//                and a (throttle-aware) "Resend verification email" button.
//   - confirmed: green "Confirmed" badge + Remove only.
function emailSection(): HTMLElement {
  let state: import('../api/types.js').EmailState =
    { email: '', verified: false, pending: false, resend_available_at: null };
  const body = h('div', {});
  let countdownTimer: ReturnType<typeof setInterval> | undefined;

  const stopCountdown = () => { if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = undefined; } };

  const render = () => {
    stopCountdown();
    if (!state.email && !state.pending && !state.verified) { renderNone(); return; }
    if (state.verified) { renderConfirmed(); return; }
    renderPending();
  };

  // --- none: add an email ---
  const renderNone = () => {
    const input = h('input', { class: 'input', type: 'email', placeholder: 'you@example.com', style: 'max-width:280px' }) as HTMLInputElement;
    const addBtn = h('button', { class: 'btn btn-primary btn-sm' }, 'Add email') as HTMLButtonElement;
    const submit = async () => {
      const email = input.value.trim();
      if (!email) { toast('Enter an email address.', 'warn'); return; }
      if (addBtn.disabled) return;
      addBtn.disabled = true;
      try {
        state = await api.auth.email.add(email);
        toast('Verification code sent', 'ok');
        render();
      } catch (e) {
        toast(emailErrorMessage((e as { code?: string }).code || '', (e as Error).message), 'danger');
        addBtn.disabled = false;
      }
    };
    addBtn.addEventListener('click', submit);
    input.addEventListener('keydown', (ev) => { if ((ev as KeyboardEvent).key === 'Enter') submit(); });
    body.replaceChildren(h('div', { class: 'row', style: 'gap:8px;align-items:center' }, input, addBtn));
  };

  // --- pending: unverified, awaiting a code ---
  const renderPending = () => {
    const emailLabel = h('span', { style: 'color:var(--fg-soft);opacity:.75' }, state.email);
    const badge = h('span', { class: 'badge badge-warn' }, 'Unverified');
    const removeBtn = rowAction('Remove', doRemove, { variant: 'danger' });
    const left = h('div', {},
      h('div', { class: 'row', style: 'gap:8px;align-items:center' }, emailLabel, badge, removeBtn),
      h('p', { class: 'muted', style: 'margin:6px 0 0' },
        h('small', {}, `We sent a code to ${state.email} — check your inbox and enter it below.`)));

    const codeInput = h('input', { class: 'input', placeholder: '6-digit code', inputmode: 'numeric', maxlength: '6', style: 'max-width:160px' }) as HTMLInputElement;
    const confirmBtn = h('button', { class: 'btn btn-primary btn-sm' }, 'Confirm') as HTMLButtonElement;
    const doConfirm = async () => {
      const code = codeInput.value.trim();
      if (!code) { toast('Enter the code from your email.', 'warn'); return; }
      if (confirmBtn.disabled) return;
      confirmBtn.disabled = true;
      try {
        state = await api.auth.email.verify(code);
        toast('Email confirmed', 'ok');
        render();
      } catch (e) {
        toast(emailErrorMessage((e as { code?: string }).code || '', (e as Error).message), 'danger');
        confirmBtn.disabled = false;
      }
    };
    confirmBtn.addEventListener('click', doConfirm);
    codeInput.addEventListener('keydown', (ev) => { if ((ev as KeyboardEvent).key === 'Enter') doConfirm(); });

    const resendBtn = h('button', { class: 'btn btn-ghost btn-sm' }, 'Resend verification email') as HTMLButtonElement;
    const syncResend = () => {
      const secs = resendSecondsRemaining(state.resend_available_at);
      if (secs > 0) {
        resendBtn.disabled = true;
        resendBtn.textContent = `Resend available in ${secs}s`;
      } else {
        resendBtn.disabled = false;
        resendBtn.textContent = 'Resend verification email';
        stopCountdown();
      }
    };
    resendBtn.addEventListener('click', async () => {
      if (resendBtn.disabled) return;
      resendBtn.disabled = true;
      try {
        state = await api.auth.email.resend();
        toast('Verification email resent', 'ok');
        syncResend();
        startCountdown(syncResend);
      } catch (e) {
        toast(emailErrorMessage((e as { code?: string }).code || '', (e as Error).message), 'danger');
        syncResend();
      }
    });

    const right = h('div', { class: 'stack', style: 'gap:8px' },
      h('div', { class: 'row', style: 'gap:8px;align-items:center' }, codeInput, confirmBtn),
      resendBtn);

    body.replaceChildren(h('div', { class: 'row between', style: 'gap:16px;align-items:flex-start;flex-wrap:wrap' }, left, right));
    syncResend();
    if (resendSecondsRemaining(state.resend_available_at) > 0) startCountdown(syncResend);
  };

  // --- confirmed: verified, Remove only ---
  const renderConfirmed = () => {
    const removeBtn = rowAction('Remove', doRemove, { variant: 'danger' });
    body.replaceChildren(h('div', { class: 'row', style: 'gap:8px;align-items:center' },
      h('strong', {}, state.email),
      h('span', { class: 'badge badge-ok' }, 'Confirmed'),
      removeBtn));
  };

  const startCountdown = (sync: () => void) => {
    stopCountdown();
    countdownTimer = setInterval(sync, 1000);
  };

  const doRemove = async () => {
    if (!confirmDialog('Remove this email address?')) return;
    try {
      await api.auth.email.remove();
      state = { email: '', verified: false, pending: false, resend_available_at: null };
      toast('Email removed', 'ok');
      render();
    } catch (e) {
      toast(emailErrorMessage((e as { code?: string }).code || '', (e as Error).message), 'danger');
    }
  };

  // Initial fetch (degrades to the empty "none" state on failure).
  const load = async () => {
    try { state = await api.auth.email.get(); } catch { /* keep empty state */ }
    render();
  };
  render();
  void load();

  const el = card('Primary email', 'Add and verify a primary email address for your account.', body);
  el.id = 'section-email';
  return el;
}

function profileSection(me: Identity): HTMLElement {
  const primary = me.username || me.handle || '';
  const out = h('div', {});

  const primaryRow = h('div', { class: 'row', style: 'align-items:center;gap:8px;margin-bottom:6px' },
    h('strong', {}, 'VirtualID: '),
    h('code', {}, primary || '—'),
    h('span', { class: 'tag', style: 'font-size:11px;opacity:.7' }, 'primary'));
  const primaryNote = h('p', { class: 'muted' },
    h('small', {}, 'Your primary username can’t be changed yet. Aliases below work the same in every link.'));

  const listEl = h('div', { class: 'stack', style: 'margin:10px 0' });
  const input = h('input', { class: 'input', placeholder: 'add another username', maxlength: '64' }) as HTMLInputElement;
  const addBtn = h('button', { class: 'btn btn-primary btn-sm' }, 'Add') as HTMLButtonElement;
  const errEl = h('p', { class: 'muted', style: 'color:var(--danger,#c33);min-height:0' });
  const limitNote = h('p', { class: 'muted' });

  const linkHint = h('p', { class: 'muted' },
    h('small', {}, primary
      ? `Your public link is virtualid.one/id/${primary} — every alias resolves to the same page.`
      : 'Add a username to get a public virtualid.one/id/<name> link.'));

  let atLimit = false;

  const render = (data: import('../api/types.js').AliasList) => {
    atLimit = data.remaining <= 0;
    const rows = data.aliases.map((a) => {
      const rm = h('button', { class: 'btn btn-ghost btn-sm', title: 'Remove alias' }, '✕') as HTMLButtonElement;
      rm.addEventListener('click', async () => {
        if (!(await confirmDialog(`Remove alias “${a.alias}”?`))) return;
        rm.disabled = true;
        try {
          await api.removeAlias(a.id);
          toast('Alias removed', 'ok');
          await refresh();
        } catch (e) { toast((e as Error).message || 'Remove failed', 'danger'); rm.disabled = false; }
      });
      return h('div', { class: 'row', style: 'align-items:center;gap:8px' }, h('code', {}, a.alias), rm);
    });
    listEl.replaceChildren(...(rows.length ? rows : [h('p', { class: 'muted' }, h('small', {}, 'No additional usernames yet.'))]));
    input.disabled = atLimit;
    addBtn.disabled = atLimit;
    limitNote.replaceChildren(atLimit
      ? h('small', {}, 'You’ve reached the maximum of 5 usernames (including your primary).')
      : h('small', {}, `You can add ${data.remaining} more (5 total including your primary).`));
  };

  const refresh = async () => {
    try {
      const data = await api.aliases();
      render(data);
    } catch (e) { toast((e as Error).message || 'Failed to load aliases', 'danger'); }
  };

  const doAdd = async () => {
    errEl.textContent = '';
    const val = input.value.trim();
    if (!val) { errEl.textContent = 'Enter a username.'; return; }
    if (atLimit) return;
    addBtn.disabled = true; addBtn.textContent = 'Adding…';
    try {
      await api.addAlias(val);
      input.value = '';
      toast('Alias added', 'ok');
      await refresh();
    } catch (e) {
      errEl.textContent = (e as Error).message || 'Could not add alias';
    } finally {
      addBtn.disabled = atLimit; addBtn.textContent = 'Add';
    }
  };
  addBtn.addEventListener('click', doAdd);
  input.addEventListener('keydown', (ev) => { if ((ev as KeyboardEvent).key === 'Enter') doAdd(); });

  out.append(
    primaryRow, primaryNote,
    h('div', { class: 'field' }, h('label', { class: 'label' }, 'Additional usernames (aliases)'), listEl),
    h('div', { class: 'row', style: 'gap:8px;align-items:center' }, input, addBtn),
    errEl, limitNote, linkHint);

  void refresh();

  return card('Account', 'Your VirtualID username and aliases. Aliases resolve exactly like your primary in every link.', out);
}

function publicSection(pub: import('../api/types.js').PublicProfile): HTMLElement {
  const out = h('div', {});
  if (pub.enabled && pub.alias) {
    const url = `https://virtualid.one/id/${pub.alias}`;
    // Real, clickable public URL that opens in a new tab, plus a Copy button.
    const copyBtn = h('button', { class: 'btn btn-ghost btn-sm', type: 'button' }, 'Copy') as HTMLButtonElement;
    copyBtn.addEventListener('click', () => {
      const done = () => toast('Public link copied', 'ok');
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(done).catch(() => toast(url, 'warn'));
      else toast(url, 'warn');
    });
    out.append(
      h('p', {}, 'Your public profile is live at ',
        h('a', { href: url, target: '_blank', rel: 'noopener noreferrer' }, url)),
      h('div', { class: 'row' },
        copyBtn,
        h('a', { class: 'btn btn-ghost btn-sm', href: '#/id/' + pub.alias }, 'Preview public profile')));
  } else {
    out.append(h('p', { class: 'muted' }, 'A profile becomes public when you mark it Public. Its page lives at virtualid.one/id/<your-handle>.'));
  }
  return card('Public profile', 'A shareable virtualid.one/id/<handle> page showing only your Public profile fields.', out);
}

function dataSection(me: Identity): HTMLElement {
  // Data export is now fully client-side (GDPR Art. 20); deletion is wired below.
  const exportBtn = h('button', { class: 'btn btn-ghost btn-sm' }, 'Export my data');
  exportBtn.addEventListener('click', async () => {
    const password = await promptEncryptionPassword();
    if (password == null) return; // cancelled — silent no-op
    exportBtn.setAttribute('disabled', 'true');
    try {
      // Gather all of the user's own data via existing authenticated calls.
      const [meFull, fieldsRes, profilesRes, subsRes, linksRes, contactsRes, totpRes, passkeysRes] =
        await Promise.all([
          api.me().catch(() => me),
          api.fields().catch(() => ({ data: [] })),
          api.profiles().catch(() => ({ data: [] })),
          api.subscribers().catch(() => ({ data: [] })),
          api.shareLinks().catch(() => ({ data: [] })),
          api.contacts().catch(() => ({ data: [] })),
          api.auth.totpList().catch(() => ({ authenticators: [] })),
          api.auth.passkeys().catch(() => ({ data: [] })),
        ]);

      const entries: { name: string; bytes: Uint8Array }[] = [];
      entries.push({ name: 'identity.json', bytes: toJsonBytes(buildIdentityJson(meFull, fieldsRes.data)) });

      // One profile_<id>.json per profile (exposures fetched per profile).
      for (const p of profilesRes.data) {
        const exposures = await api.exposures(p.id).catch(() => ({ data: [] }));
        entries.push({
          name: `profile_${p.id}.json`,
          bytes: toJsonBytes(buildProfileJson(p, exposures.data, subsRes.data, linksRes.data)),
        });
      }

      entries.push({ name: 'contacts.json', bytes: toJsonBytes(buildContactsJson(contactsRes.data)) });
      entries.push({
        name: 'settings.json',
        bytes: toJsonBytes(buildSettingsJson(totpRes.authenticators || [], passkeysRes.data)),
      });
      entries.push({ name: 'activity.json', bytes: toJsonBytes(buildActivityJson(subsRes.data, linksRes.data)) });

      const readme = new TextEncoder().encode(buildReadme());
      entries.push({ name: 'README.txt', bytes: readme });
      entries.push({ name: 'README.md', bytes: readme });
      const schema = new TextEncoder().encode(buildSchema());
      entries.push({ name: 'SCHEMA.txt', bytes: schema });
      entries.push({ name: 'SCHEMA.md', bytes: schema });

      const zip = await buildEncryptedZipMulti(entries, password);
      const username = meFull.username || meFull.handle || me.username || me.handle || '';
      downloadBytes(zip, exportFileName(username, new Date()));
      toast('Encrypted data export downloaded', 'ok');
    } catch (e) {
      toast((e as Error).message || 'Data export failed', 'danger');
    } finally {
      exportBtn.removeAttribute('disabled');
    }
  });

  const delBtn = h('button', { class: 'btn btn-sm', style: 'background:var(--danger);color:#fff' }, 'Delete account');
  delBtn.addEventListener('click', () => openDeleteModal(me));

  return card('Your data', 'GDPR data export and permanent account deletion.', h('div', { class: 'row' }, exportBtn, delBtn));
}

// openDeleteModal renders a dangerous-action confirmation: the destructive
// button stays disabled until the user types their EXACT username. On confirm
// it calls DELETE /me, then best-effort signals the authenticator to forget the
// account's passkeys, then redirects to the login/goodbye screen.
function openDeleteModal(me: Identity): void {
  const username = me.username || me.handle || '';
  if (!username) {
    toast('Account deletion needs a username on your account. Contact support.', 'warn');
    return;
  }

  const confirmBtn = h('button', {
    class: 'btn btn-sm', disabled: 'true',
    style: 'background:var(--danger);color:#fff;opacity:.5;pointer-events:none',
  }, 'Permanently delete') as HTMLButtonElement;

  const input = h('input', { class: 'input', placeholder: username, autocomplete: 'off', spellcheck: 'false' }) as HTMLInputElement;
  input.addEventListener('input', () => {
    const match = input.value === username;
    confirmBtn.disabled = !match;
    confirmBtn.style.opacity = match ? '1' : '.5';
    confirmBtn.style.pointerEvents = match ? 'auto' : 'none';
  });

  const overlay = h('div', {
    style: 'position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;z-index:1000',
  });
  const close = () => overlay.remove();
  const cancelBtn = h('button', { class: 'btn btn-ghost btn-sm' }, 'Cancel');
  cancelBtn.addEventListener('click', close);

  confirmBtn.addEventListener('click', async () => {
    if (input.value !== username) return;
    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Deleting…';
    try {
      const { credential_ids } = await api.deleteAccount(username);
      // Best-effort: hint the OS/password manager to remove the local passkeys.
      // Never blocks or fails the deletion (websites cannot force-remove a
      // platform passkey; this is the sanctioned Signal API hint).
      try {
        await runPasskeySignals(
          { rpId: config.rpId, credentialIds: credential_ids },
          browserSignaller(),
        );
      } catch { /* best-effort only */ }
      close();
      toast('Your account has been permanently deleted.', 'ok');
      // Redirect to the goodbye/login screen.
      window.location.hash = '#/login';
      window.location.reload();
    } catch (e) {
      confirmBtn.disabled = false;
      confirmBtn.textContent = 'Permanently delete';
      toast((e as Error).message || 'Deletion failed', 'danger');
    }
  });

  const modal = h('div', { class: 'card', style: 'max-width:460px;width:90%' },
    h('h3', { style: 'margin-top:0;color:var(--danger)' }, 'Delete your VirtualID'),
    h('p', {}, 'This permanently deletes your VirtualID, all profiles, fields, and removes your subscribers’ access. This cannot be undone.'),
    h('p', { class: 'muted' }, h('small', {}, 'Type your username ', h('strong', {}, username), ' to confirm.')),
    h('div', { class: 'field' }, input),
    h('div', { class: 'row', style: 'justify-content:flex-end;gap:8px;margin-top:12px' }, cancelBtn, confirmBtn),
  );
  overlay.append(modal);
  document.body.append(overlay);
  input.focus();
}
